/**
 * TheNewsPulse.pro — modern header + horizontal top nav + minimal footer.
 * Layout is scoped: only mounts when siteConfig resolves to `newspulse`.
 */
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Globe2, Search, Bell, User, ChevronDown, Menu, X, Radio,
} from "lucide-react";
import { TOP_NAV, ALL_TOPICS } from "./theme";
import BreakingTicker from "./BreakingTicker";
import NewsPulseAlertToast from "./NewsPulseAlertToast";
import NotificationBell from "@/components/NotificationBell";
import BecomeContributorCTA from "@/components/newspulse/BecomeContributorCTA";

export default function NewsPulseLayout() {
  const loc = useLocation();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}
      data-testid="newspulse-layout"
    >
      <BreakingTicker />
      {/* ── Top header ── */}
      <header className="bg-white border-b border-slate-200" data-testid="newspulse-header">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 h-16 flex items-center gap-4 lg:gap-8">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0"
            data-testid="newspulse-logo"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-sm">
              <Globe2 className="w-6 h-6 text-white" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="text-lg font-bold tracking-tight text-slate-900">
                TheNewsPulse<span className="text-blue-600">.pro</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 hidden sm:block">
                One World. Every Story. For a Smarter Tomorrow.
              </div>
            </div>
          </Link>

          {/* Header search */}
          <form
            onSubmit={onSearch}
            className="flex-1 max-w-xl hidden md:flex items-center bg-slate-100 rounded-full pl-4 pr-1 py-1 border border-transparent focus-within:border-blue-500 focus-within:bg-white transition-colors"
            data-testid="newspulse-header-search-form"
          >
            <Search className="w-4 h-4 text-slate-400 mr-2" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search news, topics, places or people…"
              className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-slate-400"
              data-testid="newspulse-header-search-input"
            />
            <button
              type="submit"
              className="rounded-full w-8 h-8 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
              aria-label="Search"
              data-testid="newspulse-header-search-submit"
            >
              <Search className="w-4 h-4" />
            </button>
          </form>

          {/* Header actions */}
          <div className="ml-auto flex items-center gap-1 md:gap-3">
            <button
              type="button"
              className="hidden md:flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
              data-testid="newspulse-lang-toggle"
            >
              <Globe2 className="w-4 h-4" />
              <span className="font-medium">EN</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            <Link
              to="/for-you"
              className="hidden md:flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
              data-testid="newspulse-my-news"
            >
              <Bell className="w-4 h-4" />
              <span className="font-medium">My News</span>
            </Link>
            {/* Global notification bell (chat replies, article replies,
                weather alerts, messages) — signed-in only. */}
            <NotificationBell />
            <button
              type="button"
              className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors"
              aria-label="Account"
              data-testid="newspulse-account"
            >
              <User className="w-4 h-4 text-slate-600" />
            </button>
            <button
              type="button"
              className="md:hidden w-9 h-9 rounded-md flex items-center justify-center hover:bg-slate-100"
              aria-label="Toggle menu"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="newspulse-mobile-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Top nav bar */}
        <nav
          className="border-t border-slate-200 bg-white hidden md:block"
          data-testid="newspulse-nav"
        >
          <div className="max-w-[1280px] mx-auto px-4 lg:px-6 h-12 flex items-center gap-6">
            {TOP_NAV.map((n) => {
              const active = loc.pathname === n.to
                || (n.to !== "/" && loc.pathname.startsWith(n.to));
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`relative text-sm font-medium py-3 transition-colors ${
                    active
                      ? "text-blue-600"
                      : "text-slate-700 hover:text-slate-900"
                  }`}
                  data-testid={`newspulse-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                  {active && (
                    <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-blue-600 rounded-full" />
                  )}
                </Link>
              );
            })}
            <div className="relative" data-testid="newspulse-nav-more-wrap">
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                className="text-sm font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1"
                data-testid="newspulse-nav-more"
              >
                All Topics <ChevronDown className={`w-3 h-3 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setMoreOpen(false)}
                  />
                  <div
                    className="absolute right-0 top-full mt-2 z-50 w-[min(90vw,880px)] bg-white border border-slate-200 rounded-2xl shadow-xl p-6"
                    data-testid="newspulse-all-topics"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
                      {ALL_TOPICS.map((g) => (
                        <div key={g.group}>
                          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2 pb-1 border-b border-slate-100">
                            {g.group}
                          </div>
                          <ul className="space-y-1.5">
                            {g.items.map(([label, path]) => (
                              <li key={path}>
                                <Link
                                  to={path}
                                  onClick={() => setMoreOpen(false)}
                                  className="text-sm text-slate-700 hover:text-blue-700 transition-colors block"
                                  data-testid={`all-topics-link-${path}`}
                                >
                                  {label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <Link
              to="/breaking"
              className="ml-auto inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors shadow-sm"
              data-testid="newspulse-nav-breaking"
            >
              <Radio className="w-4 h-4" /> Breaking News
            </Link>
            <BecomeContributorCTA variant="pill" className="hidden lg:inline-flex" />
          </div>
        </nav>

        {/* Mobile nav sheet */}
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white" data-testid="newspulse-mobile-nav">
            <div className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2">
              {TOP_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `text-sm py-2 ${isActive ? "text-blue-600 font-semibold" : "text-slate-700"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
            <form
              onSubmit={(e) => { onSearch(e); setMobileOpen(false); }}
              className="px-4 pb-4 flex items-center bg-slate-100 rounded-full mx-4 mb-4 pl-4 pr-1"
            >
              <Search className="w-4 h-4 text-slate-400 mr-2" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="flex-1 bg-transparent border-0 outline-none text-sm py-2"
                data-testid="newspulse-mobile-search-input"
              />
            </form>
          </div>
        )}
      </header>

      <main data-testid="newspulse-main">
        <Outlet />
      </main>

      {/* Global weather / crop advisory toast (auto-shows for readers
          whose region has an active alert in city-strip). */}
      <NewsPulseAlertToast />

      {/* ── Footer ── */}
      <footer className="border-t border-slate-200 bg-slate-50 mt-16" data-testid="newspulse-footer">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8 flex flex-wrap items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
              <Globe2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-900">TheNewsPulse<span className="text-blue-600">.pro</span></span>
          </Link>
          <span className="text-xs text-slate-500">
            © 2026 · Central news intelligence for the SocialPulse network
          </span>
          <Link
            to="/about"
            className="ml-auto text-xs uppercase tracking-widest text-slate-600 hover:text-blue-600 transition-colors"
          >
            About & Methodology →
          </Link>
        </div>
      </footer>
    </div>
  );
}
